import React from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import Validator from '../../../shared/validator';
import Loading from '../../../shared/loading';
import parseFailXhr from '../../../shared/parse-fail-xhr';
import { EMAIL_REGEX } from '../../../shared/consts';
import mutate from '../../../shared/mutate';
import ErrorBoundary from '../../../shared/error-boundary';
import { contactAsync } from '../../../services/contact-services';
import { FormContainer } from './styles';

export default class ContactForm extends React.Component {
  static propTypes = {
    hideSubmit: PropTypes.bool,
    message: PropTypes.string,
    onAfterSubmit: PropTypes.func,
  };

  static defaultProps = {
    hideSubmit: false,
    message: '',
    onAfterSubmit: null,
  };

  constructor(props, context) {
    super(props, context);

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleCompanyChange = this.handleCompanyChange.bind(this);
    this.handleEmailAddressChange = this.handleEmailAddressChange.bind(this);
    this.handleMobileChange = this.handleMobileChange.bind(this);
    this.handleSendMediaKitChange = this.handleSendMediaKitChange.bind(this);
    this.handleMessageChange = this.handleMessageChange.bind(this);

    this.validator = new Validator({
      nameRequired: state => !!state.name,
      emailAddressRequired: state => !!state.emailAddress,
      emailAddressRegex: state => !state.emailAddress || EMAIL_REGEX.test(state.emailAddress),
      messageRequired: state => !!state.message,
    });

    this.state = {
      messageSent: false,
      name: '',
      company: '',
      emailAddress: '',
      mobile: '',
      sendMediaKit: false,
      message: this.props.message || '',
      validations: this.validator.getValidations(),
    };
  }

  handleNameChange(event) {
    const name = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.name = name;
      s.validations.nameRequiredValid = this.validator.validateNameRequired(s);
    }));
  }

  handleCompanyChange(event) {
    const company = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.company = company;
    }));
  }

  handleEmailAddressChange(event) {
    const emailAddress = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.emailAddress = emailAddress;
      s.validations.emailAddressRequiredValid = this.validator.validateEmailAddressRequired(s);
      s.validations.emailAddressRegexValid = this.validator.validateEmailAddressRegex(s);
    }));
  }

  handleMobileChange(event) {
    const mobile = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.mobile = mobile;
    }));
  }

  handleSendMediaKitChange(event) {
    const sendMediaKit = event.target.checked;

    this.setState(oldState => mutate(oldState, s => {
      s.sendMediaKit = sendMediaKit;
    }));
  }

  handleMessageChange(event) {
    const message = event.target.value;

    this.setState(oldState => mutate(oldState, s => {
      s.message = message;
      s.validations.messageRequiredValid = this.validator.validateMessageRequired(s);
    }));
  }

  clear() {
    this.validator.resetValidation();
    this.setState({
      messageSent: false,
      name: '',
      company: '',
      emailAddress: '',
      mobile: '',
      message: this.props.message || '',
      sendMediaKit: false,
      validations: this.validator.getValidations(),
    });
  }

  submit(event) {
    event && event.preventDefault && event.preventDefault();

    this.setState(
      state => ({
        validations: this.validator.validateForm(state),
      }),
      () => {
        if (!this.validator.formValid) return;

        this.loading.start();
        contactAsync({
          name: this.state.name,
          company: this.state.company,
          emailAddress: this.state.emailAddress,
          mobile: this.state.mobile,
          message: this.state.message,
          sendMediaKit: this.state.sendMediaKit,
        })
          .done(() => {
            this.validator.resetValidation();
            this.setState(s => mutate(s, ss => {
              ss.messageSent = true;
              ss.validations = this.validator.getValidations();
            }));
            toastr.success('Your message has been sent');
            this.props.onAfterSubmit && this.props.onAfterSubmit();
          })
          .fail(xhr => {
            const errors = parseFailXhr(xhr);

            errors.anyUnhandledErrors() && toastr.error(errors.getErrorSummary(), 'Error');
          })
          .always(() => this.loading.stop());
      },
    );
  }

  renderName() {
    const isValid = this.state.validations.nameRequiredValid;
    return (
      <p className={`form-group ${isValid || 'has-error'}`}>
        <input
          type="text"
          placeholder="Name"
          value={this.state.name}
          onChange={this.handleNameChange}
        />
        {isValid || <span className="help-block">Name is required</span>}
      </p>
    );
  }

  renderCompany() {
    return (
      <p className="form-group">
        <input
          type="text"
          placeholder="Company"
          value={this.state.company}
          onChange={this.handleCompanyChange}
        />
      </p>
    );
  }

  renderMobile() {
    return (
      <p className="form-group">
        <input
          type="text"
          placeholder="Mobile"
          value={this.state.mobile}
          onChange={this.handleMobileChange}
        />
      </p>
    );
  }

  renderEmail() {
    const { emailAddressRequiredValid, emailAddressRegexValid } = this.state.validations;
    return (
      <p className={`form-group ${emailAddressRequiredValid && emailAddressRegexValid || 'has-error'}`}>
        <input
          type="email"
          placeholder="Email"
          value={this.state.emailAddress}
          onChange={this.handleEmailAddressChange}
        />
        {emailAddressRequiredValid || <span className="help-block">Email is required</span>}
        {emailAddressRegexValid || <span className="help-block">Email is invalid</span>}
      </p>
    );
  }

  renderMessage() {
    const isValid = this.state.validations.messageRequiredValid;
    return (
      <p className={`form-group ${isValid || 'has-error'}`}>
        <textarea
          placeholder="Message"
          value={this.state.message}
          onChange={this.handleMessageChange}
        />
        {isValid || <span className="help-block">Message is required</span>}
      </p>
    );
  }

  render() {
    return (
      <ErrorBoundary>
        <FormContainer className="contact-form">
          <div className="prompt">Expect a prompt reply – we&apos;re committed to connecting with you quickly.</div>
          <form onSubmit={e => this.submit(e)}>
            <Loading ref={el => (this.loading = el)} variant="small">
              {this.renderName()}
              {this.renderCompany()}
              {this.renderEmail()}
              {this.renderMobile()}
              <div className="message-prompt">Tell us how you&apos;d like to partner – through ads, content, or a combination.</div>
              {this.renderMessage()}
              {this.props.hideSubmit || (
                <button type="submit" className="btn btn-primary">Get In Touch</button>
              )}
            </Loading>
          </form>
          {!this.state.messageSent || (
            <div className="alert alert-success">
              <strong>Success!</strong>
              {' Your message has been sent to the team at Livewire.'}
            </div>
          )}
        </FormContainer>
      </ErrorBoundary>
    );
  }
}
